// Global Instructions Rule Applied!

import {
  validateFilterOptions,
  type TableFilterOptionsResponse
} from '../../../state_manager/rpc_models/get_table_filter_options_response.ts'
import {
  validateContactRecord,
  type ContactRecord,
  ContactRecordFields
} from '../../../state_manager/data_models/contact_record.ts'
import { FilterValue, SortConfig } from '../../../state_manager/store_models/contacts.ts'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { RELATIONSHIP_TYPE_FOUNDER } from '../../../state_manager/globals.ts'
import { ACTION_TYPE_INSERT, ACTION_TYPE_DELETE } from '../../../state_manager/globals.ts'
import { CompanyTeamEntryFields } from '../../../state_manager/data_models/company_team_entry.ts'
import { formatKeyToLabel, readBackendErrorMessage } from '../../../core/utils/utilities.ts'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { TOAST_CONFIG } from '../../../core/components/FormTable/constants.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface ContactsPaginatedResponse {
  data: ContactRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

/**
 * Fetches contacts with pagination support for infinite scroll
 * @param page - Page number (1-based)
 * @param pageSize - Number of records per page (default: 200)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with contacts data, pagination info, and validation
 */
export const fetchContactsPaginated = async (
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null,
  reportId?: string | null
): Promise<ContactsPaginatedResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(
      buildFullUrl(
        BACKEND_BASE_URL,
        API_ROUTES.CONTACTS.LIST,
        {},
        {
          ...(pageSize !== null && { page: page.toString(), pageSize: pageSize.toString() }),
          ...(reportId ? { report_id: reportId } : {})
        }
      ),
      {
        method: API_ROUTES.CONTACTS.LIST.method,
        headers
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch contacts: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[]; has_more?: boolean; count?: number }

    const validatedData = validateContactRecord(parsed.data || [], { isArray: true }) as ContactRecord[]

    return {
      data: validatedData,
      hasMore: parsed.has_more || false,
      totalCount: parsed.count || 0
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Unexpected error fetching paginated contacts:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching contacts'
    }
  }
}

/**
 * Fetches contacts with filtering and sorting using POST /contacts/filter
 * @param sorting - Sort configuration
 * @param filters - Array of filter values
 * @param page - Page number (1-based)
 * @param pageSize - Number of records per page (default: 200)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with contacts data, pagination info, and validation
 */
export const fetchContactsFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null,
  reportId?: string | null
): Promise<ContactsPaginatedResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const effectivePageSize = pageSize ?? 200

    const requestBody: any = {
      data: {
        page,
        page_size: effectivePageSize
      }
    }

    // Add sorting if provided
    requestBody.data.sorting = sorting.map((sort) => ({
      key: sort.key,
      direction: sort.direction
    }))

    // Add filters if provided
    if (filters.length > 0) {
      requestBody.data.filters = filters.map((filter) => ({
        key: filter.key,
        value: filter.value
      }))
    } else {
      requestBody.data.filters = []
    }

    if (reportId) {
      requestBody.data.report_id = reportId
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONTACTS.FILTER), {
      method: API_ROUTES.CONTACTS.FILTER.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch filtered contacts: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[]; has_more?: boolean; count?: number }

    const validatedData = validateContactRecord(parsed.data || [], { isArray: true }) as ContactRecord[]

    return {
      data: validatedData,
      hasMore: parsed.has_more || false,
      totalCount: parsed.count || 0
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Unexpected error fetching filtered contacts:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching contacts'
    }
  }
}

export const fetchContactsFilterOptions = async (
  accessToken: string | null = null,
  requestedKeys?: string[]
): Promise<TableFilterOptionsResponse> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  const body: Record<string, unknown> = { data: {} }
  if (requestedKeys && requestedKeys.length > 0) {
    body.data = { requested_keys: requestedKeys }
  }

  const res = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONTACTS.FILTER_OPTIONS), {
    method: API_ROUTES.CONTACTS.FILTER_OPTIONS.method,
    headers,
    body: JSON.stringify(body)
  })

  if (!res.ok) {
    if (res.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(`Failed to fetch filter options: ${res.status} ${res.statusText}`)
  }

  const json: unknown = await res.json()
  const parsed = json as { data?: Record<string, unknown> }
  return { data: validateFilterOptions(parsed.data || {}) }
}

export interface CreateContactResponse {
  data: ContactRecord | null
  created_ids?: Record<string, string>
  error?: string
}

/**
 * Creates a new contact with validation
 * @param contactData - The contact data to create (should be ContactRecord format)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with validated contact record or error
 */
export const createContact = async (
  contactData: Record<string, any>,
  accessToken: string | null = null
): Promise<CreateContactResponse> => {
  try {
    const validContactRecordFields = new Set<string>(Object.values(ContactRecordFields))
    const cleanedData: Record<string, any> = {}

    Object.keys(contactData).forEach((key) => {
      if (validContactRecordFields.has(key)) {
        cleanedData[key] = contactData[key]
      }
    })

    // Remove temporary account_id field (if present) - the schema key is contact_accounts__account_id
    const dataForValidation = { ...cleanedData }
    if (dataForValidation.prog__founder_of && Array.isArray(dataForValidation.prog__founder_of)) {
      dataForValidation.prog__founder_of = dataForValidation.prog__founder_of.map((entry: any) => {
        const { account_id, ...entryWithoutAccountId } = entry
        return entryWithoutAccountId
      })
    }
    if (dataForValidation.prog__introductions && Array.isArray(dataForValidation.prog__introductions)) {
      dataForValidation.prog__introductions = dataForValidation.prog__introductions.map((entry: any) => {
        const { account_id, ...entryWithoutAccountId } = entry
        return entryWithoutAccountId
      })
    }

    try {
      validateContactRecord(dataForValidation)
    } catch (validationError) {
      return {
        data: null,
        error: validationError instanceof Error ? validationError.message : 'Invalid contact data provided'
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    // Use cleanedData (account ids travel as contact_accounts__account_id, which is on the schema)
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONTACTS.CREATE), {
      method: API_ROUTES.CONTACTS.CREATE.method,
      headers,
      body: JSON.stringify(cleanedData)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 400) {
        const errorResult = await response.json()

        // Check if this is a duplicate error
        if (errorResult.message === 'Duplicate records found' && errorResult.data) {
          // Show toast for each duplicate field
          Object.entries(errorResult.data).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
              const formattedLabel = formatKeyToLabel(key, true, true)
              const duplicateValues = value.join(', ')
              showErrorToast(`Duplicate ${formattedLabel} found: ${duplicateValues}`, TOAST_CONFIG)
            }
          })
        }

        // Still throw error to prevent form submission
        throw new Error(await readBackendErrorMessage(response, 'Failed to create contact', errorResult))
      }
      // Any other failure: the backend's own sentence, never a bare status line.
      throw new Error(await readBackendErrorMessage(response, 'Failed to create contact'))
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown; created_ids?: Record<string, string> }

    if (parsed.data) {
      const validatedData = validateContactRecord(parsed.data)
      return {
        data: validatedData,
        created_ids: parsed.created_ids
      }
    }

    return {
      data: null,
      error: 'No data returned from server'
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error creating contact:', error)
    }
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while creating contact'
    }
  }
}

export interface FetchContactByIdResponse {
  data: ContactRecord | null
  error?: string
}

/**
 * Fetches a single contact by ID
 * @param contactId - The ID of the contact to fetch
 * @param accessToken - User's access token from Redux store
 * @returns Promise with validated contact record or error
 */
export const fetchContactById = async (
  contactId: string | number,
  accessToken: string | null = null
): Promise<FetchContactByIdResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONTACTS.GET, { id: contactId }), {
      method: API_ROUTES.CONTACTS.GET.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 404) {
        throw new Error('Contact not found')
      }
      throw new Error(`Failed to fetch contact: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown }

    if (parsed.data) {
      const validatedData = validateContactRecord(parsed.data)
      return {
        data: validatedData
      }
    }

    return {
      data: null,
      error: 'No data returned from server'
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching contact by ID:', error)
    }
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching contact'
    }
  }
}

export const deleteContact = async (contactId: string | number, accessToken: string | null = null): Promise<void> => {
  const headers: Record<string, string> = {}
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }
  const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONTACTS.DELETE, { id: contactId }), {
    method: API_ROUTES.CONTACTS.DELETE.method,
    headers
  })
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication failed - please log in again')
    }
    const errorBody = await response.json().catch(() => ({}))
    throw new Error((errorBody as { message?: string }).message || 'Failed to delete contact')
  }
}

export interface UpdateContactResponse {
  data: ContactRecord | null
  error?: string
}

/**
 * Updates an existing contact
 * Note: For form field updates, InputManager auto-patch is preferred.
 * This function is kept for programmatic updates.
 * @param contactId - The ID of the contact to update
 * @param contactData - The contact data to update (partial updates supported)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with validated contact record or error
 */
export const updateContact = async (
  contactId: string | number,
  contactData: Record<string, any>,
  accessToken: string | null = null
): Promise<UpdateContactResponse> => {
  try {
    const validContactRecordFields = new Set<string>(Object.values(ContactRecordFields))
    const cleanedData: Record<string, any> = {}

    Object.keys(contactData).forEach((key) => {
      if (validContactRecordFields.has(key)) {
        cleanedData[key] = contactData[key]
      }
    })

    try {
      validateContactRecord(cleanedData)
    } catch (validationError) {
      return {
        data: null,
        error: validationError instanceof Error ? validationError.message : 'Invalid contact data provided'
      }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    cleanedData.id = contactId

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONTACTS.PATCH), {
      method: API_ROUTES.CONTACTS.PATCH.method,
      headers,
      body: JSON.stringify(cleanedData)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 404) {
        throw new Error('Contact not found')
      }
      if (response.status === 400) {
        const errorResult = await response.json()
        throw new Error((errorResult as { message?: string }).message || 'Invalid data provided')
      }
      throw new Error(`Failed to update contact: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown }

    if (parsed.data) {
      const validatedData = validateContactRecord(parsed.data)
      return {
        data: validatedData
      }
    }

    return {
      data: null,
      error: 'No data returned from server'
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error updating contact:', error)
    }
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while updating contact'
    }
  }
}

export interface SearchContactsForPopupResponse {
  data: ContactRecord[]
  error?: string
}

/**
 * Searches contacts for toolbar search popup (independent of table filters)
 * @param searchTerm - The search term to look for
 * @param limit - Maximum number of results to return (default: 10)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with validated contact records or error
 */
export const searchContactsForPopup = async (
  searchTerm: string,
  limit: number = 10,
  accessToken: string | null = null
): Promise<SearchContactsForPopupResponse> => {
  try {
    if (!searchTerm || searchTerm.trim() === '') {
      return { data: [] }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const queryParams = new URLSearchParams({
      search: searchTerm.trim(),
      limit: limit.toString()
    })

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONTACTS.SEARCH, {}, Object.fromEntries(queryParams)),
      {
        method: API_ROUTES.CONTACTS.SEARCH.method,
        headers
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to search contacts: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[] }

    if (parsed.data) {
      const limitedData = parsed.data.slice(0, limit)
      // Transform API response format to ContactRecord format
      const transformedData = limitedData.map((item: any) => ({
        [ContactRecordFields.contacts__id]: item.value || null,
        [ContactRecordFields.contacts__first_name]: item.first_name || null,
        [ContactRecordFields.contacts__last_name]: item.last_name || null,
        [ContactRecordFields.contacts__preferred_name]: item.preferred_name || null,
        [ContactRecordFields.contacts__email]: item.email || null
      }))
      const validatedData = validateContactRecord(transformedData, { isArray: true }) as ContactRecord[]
      return {
        data: validatedData
      }
    }

    return {
      data: []
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error searching contacts for popup:', error)
    }
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred while searching contacts'
    }
  }
}

/**
 * Processes contact-company relationships (founder_of, introductions) using the same API as CompanyTeamTable
 * @param contactId - The ID of the contact
 * @param companyId - The ID of the company (account)
 * @param relationships - Array of relationship entries in CompanyTeamEntrySchema format
 * @param accessToken - User's access token
 * @returns Promise with API response
 */
export const processContactCompanyRelationships = async (
  contactId: string,
  companyId: string,
  relationships: any[],
  accessToken: string | null = null
): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'account-id': companyId
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONTACT_RELATIONSHIPS.PROCESS_TEAM), {
      method: API_ROUTES.CONTACT_RELATIONSHIPS.PROCESS_TEAM.method,
      headers,
      body: JSON.stringify(relationships)
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP ${response.status}`)
    }

    const result = await response.json()
    // Return result directly - it already has success/error/data structure
    return result
  } catch (error) {
    console.error('Error processing contact-company relationships:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}

/**
 * Transforms founder_of company selections to API format for processContactCompanyRelationships
 * Returns a map of companyId -> relationships array in CompanyTeamEntrySchema format
 * @param contactId - The ID of the contact
 * @param currentEntries - Current founder_of entries from formData (in CompanyTeamEntrySchema format)
 * @param newSelections - New company selections from InputManager
 * @returns Map of companyId to array of relationship entries in CompanyTeamEntrySchema format
 */
export const transformFounderOfToAPIFormat = (
  contactId: string,
  currentEntries: any[],
  newSelections: any[]
): Map<string, any[]> => {
  const relationshipsByCompany = new Map<string, any[]>()

  // Use CompanyTeamEntryFields constants
  const CONTACTS_ID = CompanyTeamEntryFields.contacts__id
  const CONTACT_ACCOUNTS_ID = CompanyTeamEntryFields.contact_accounts__id
  const CONTACT_ACCOUNTS_ACCOUNT_ID = CompanyTeamEntryFields.contact_accounts__account_id
  const PROG_CONTACT_ACCOUNTS_ACTION = CompanyTeamEntryFields.prog__contact_accounts__action
  const PROG_FOUNDER = CompanyTeamEntryFields.prog__founder

  // Get current company IDs - convert to strings for consistent comparison
  const currentCompanyIds = new Set(
    currentEntries
      .map((entry) => {
        const id = entry[CONTACT_ACCOUNTS_ACCOUNT_ID] || entry.prog__account_id || entry.account_id
        return id ? String(id) : null
      })
      .filter((id): id is string => Boolean(id))
  )

  // Get new company IDs - convert to strings for consistent comparison
  const newCompanyIds = new Set(
    newSelections
      .map((sel) => {
        const id = sel.value || sel.id
        return id ? String(id) : null
      })
      .filter((id): id is string => Boolean(id))
  )

  // Find companies to add (in new but not in current)
  const toAdd = Array.from(newCompanyIds).filter((id) => !currentCompanyIds.has(id))

  // Find companies to remove (in current but not in new)
  const toRemove = Array.from(currentCompanyIds).filter((id) => !newCompanyIds.has(id))

  // Create INSERT relationships for new companies (in CompanyTeamEntrySchema format)
  toAdd.forEach((companyId) => {
    const relationships = relationshipsByCompany.get(companyId) || []
    relationships.push({
      [CONTACTS_ID]: contactId,
      [CONTACT_ACCOUNTS_ID]: null, // New relationship, no ID yet
      [CONTACT_ACCOUNTS_ACCOUNT_ID]: companyId, // Include company ID in payload
      [PROG_CONTACT_ACCOUNTS_ACTION]: ACTION_TYPE_INSERT,
      [PROG_FOUNDER]: true
    })
    relationshipsByCompany.set(companyId, relationships)
  })

  // Create DELETE relationships for removed companies (in CompanyTeamEntrySchema format)
  toRemove.forEach((companyId) => {
    // Find the existing entry to get the relationship ID
    const existingEntry = currentEntries.find((entry) => {
      const entryAccountId = entry[CONTACT_ACCOUNTS_ACCOUNT_ID] || entry.prog__account_id || entry.account_id
      return entryAccountId ? String(entryAccountId) === String(companyId) : false
    })

    // Only create DELETE if we have a contact_accounts__id (relationship exists in DB)
    // Skip if this entry was just inserted (has action=insert) - it shouldn't be deleted
    if (existingEntry?.[CONTACT_ACCOUNTS_ID] && existingEntry[PROG_CONTACT_ACCOUNTS_ACTION] !== ACTION_TYPE_INSERT) {
      const relationships = relationshipsByCompany.get(companyId) || []
      relationships.push({
        [CONTACT_ACCOUNTS_ID]: existingEntry[CONTACT_ACCOUNTS_ID],
        [PROG_CONTACT_ACCOUNTS_ACTION]: ACTION_TYPE_DELETE
      })
      relationshipsByCompany.set(companyId, relationships)
    } else if (!existingEntry?.[CONTACT_ACCOUNTS_ID]) {
      // Log warning if we can't find the relationship ID for deletion
      console.warn(`Cannot delete founder relationship: No contact_accounts__id found for company ${companyId}`)
    }
    // Silently skip if entry has action=insert (it was just added, shouldn't be deleted)
  })

  return relationshipsByCompany
}

export interface FetchAllContactsResponse {
  data: ContactRecord[]
  totalCount: number
  hasMore: boolean
  error?: string
}

/**
 * Fetches all contacts for dropdown usage (returns all records)
 * Compatible with old contacts_deprecated service
 * @param accessToken - User's access token from Redux store
 * @param page - Page number (default: 1)
 * @param pageSize - Number of items per page (null = all records)
 * @param filters - Filter options (legacy format)
 * @param sortConfig - Sort configuration (legacy format)
 * @returns Promise with contacts data
 */
export const fetchAllContacts = async (
  accessToken: string | null = null,
  page: number = 1,
  pageSize: number | null = null,
  filters: Record<string, any> = {},
  sortConfig: { column?: string; direction?: string } = {}
): Promise<FetchAllContactsResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const queryParams: Record<string, string> = {
      page: page.toString()
    }

    if (pageSize !== null) {
      queryParams.pageSize = pageSize.toString()
    }

    if (sortConfig.column) {
      queryParams.sortColumn = sortConfig.column
      queryParams.sortDirection = sortConfig.direction || 'asc'
    }

    if (filters && Object.keys(filters).length > 0) {
      queryParams.filters = JSON.stringify(filters)
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONTACTS.LIST, {}, queryParams), {
      method: API_ROUTES.CONTACTS.LIST.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch contacts: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[]; has_more?: boolean; count?: number; totalCount?: number }

    const validatedData = validateContactRecord(parsed.data || [], { isArray: true }) as ContactRecord[]

    return {
      data: validatedData,
      totalCount: parsed.totalCount || parsed.count || 0,
      hasMore: parsed.has_more || false
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching all contacts:', error)
    }
    return {
      data: [],
      totalCount: 0,
      hasMore: false,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching contacts'
    }
  }
}
